/**
 * Resolves the per-agency backend URLs.
 *
 * config/environments/*.env holds templates rather than one tenant's host:
 *
 *   API_BASE_URL=https://{agency}.fastertech.ai/technicianappbff/
 *   NOVA_AI_BASE_URL=https://{agency}.fastertech.ai/nova-backend/
 *
 * `{agency}` is filled in on every call, so one build serves every agency:
 *
 *   Web, deployed  the page's own origin + the template's path. The app is
 *                  opened from {agency}.<domain>, and that host routes
 *                  /technicianappbff/ and /nova-backend/, so the domain in
 *                  the template is never needed here.
 *   Web, localhost the template with DEV_AGENCY_NAME, or as-is when it holds
 *                  no placeholder (the local proxy setup in .env.web).
 *   iOS / Android  the template with the agency name the user entered on
 *                  AgencySelectionScreen (agencyStorage).
 *
 * `{agency}` and not `${AGENCY}`: a `$` is interpolated by dotenv-expand, by
 * react-native-config's xcconfig on iOS, and by Gradle.
 */
import {Platform} from 'react-native';
import {getConfig} from './AppConfig';
import {agencyStorage} from '../../features/auth/api/agencyStorage';

export const AGENCY_PLACEHOLDER = '{agency}';

// One DNS label. This is what stops a typed agency name such as
// "evil.com/#" from sending API traffic, and the auth cookie, to another host.
const AGENCY_LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0'];

/** Drops trailing slashes without a regex (Sonar flags /\/+$/ as a backtracking risk). */
export const stripTrailingSlashes = (value: string): string => {
  let end = value.length;
  while (end > 0 && value[end - 1] === '/') {
    end--;
  }
  return value.slice(0, end);
};

/** Thrown when a URL needs an agency and none is known yet. */
export class AgencyNotSetError extends Error {
  constructor(message = 'No agency is set — choose one on AgencySelectionScreen first.') {
    super(message);
    this.name = 'AgencyNotSetError';
  }
}

/** The agency name as a hostname label ("Transit-QA" → "transit-qa"), or null when it cannot be one. */
export const toAgencyLabel = (name?: string | null): string | null => {
  const label = (name ?? '').trim().toLowerCase();
  return AGENCY_LABEL.test(label) ? label : null;
};

const isLocalHost = (hostname: string): boolean => LOCAL_HOSTS.includes(hostname);

/**
 * Web only: the agency is the first label of the host the app was opened from
 * (transit-qa.fastertech.ai → "transit-qa"). On localhost there is no
 * subdomain, so DEV_AGENCY_NAME from .env.web stands in for it.
 */
export const getWebAgency = (): string | null => {
  const hostname =
    typeof window !== 'undefined' ? window.location?.hostname : undefined;
  if (!hostname) {
    return null;
  }
  if (isLocalHost(hostname)) {
    return process.env.DEV_AGENCY_NAME || null;
  }
  const isNumericIp = /^\d+(\.\d+){3}$/.test(hostname);
  const parts = hostname.split('.');
  if (isNumericIp || parts.length < 2) {
    return null;
  }
  return parts[0] ?? null;
};

const fillTemplate = (template: string, agency?: string | null): string => {
  if (!template.includes(AGENCY_PLACEHOLDER)) {
    return template;
  }
  const label = toAgencyLabel(agency);
  if (!label) {
    throw new AgencyNotSetError(
      agency
        ? `"${agency}" cannot be used as an agency host name.`
        : undefined,
    );
  }
  return template.split(AGENCY_PLACEHOLDER).join(label);
};

/**
 * Turns a URL template from the environment into the URL to call, without a
 * trailing slash. `agency` overrides the current one — used while validating
 * a name the user has typed but that is not stored yet.
 */
export const resolveAgencyUrl = (
  template: string | undefined | null,
  agency?: string | null,
): string => {
  const trimmed = stripTrailingSlashes((template ?? '').trim());

  if (Platform.OS === 'web') {
    const hostname = typeof window !== 'undefined' ? window.location?.hostname : undefined;
    if (hostname && !isLocalHost(hostname)) {
      // Deployed: always call the host the page was opened from, whatever the
      // template's domain says, so dev, qa, uat and prod need no domain at all.
      let path = trimmed;
      try {
        path = new URL(trimmed).pathname;
      } catch {
        // Not an absolute URL — treat the value as a path already.
      }
      path = stripTrailingSlashes(path);
      if (path && !path.startsWith('/')) {
        path = `/${path}`;
      }
      return `${window.location.origin}${path}`;
    }
    return fillTemplate(trimmed, agency ?? getWebAgency());
  }

  return fillTemplate(trimmed, agency ?? agencyStorage.get());
};

/** BFF base URL for the current (or the given) agency. */
export const getApiBaseUrl = (agency?: string | null): string =>
  resolveAgencyUrl(getConfig()?.API_BASE_URL, agency);

/** Nova backend base URL for the current (or the given) agency. */
export const getNovaBaseUrl = (agency?: string | null): string =>
  resolveAgencyUrl(getConfig()?.NOVA_AI_BASE_URL, agency);
